import { useState } from "react"
import { Box, Link, List, Stack, Typography } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { sortBy } from "lib/sortBy"
import { useAuthoringLegislationLibrary } from "../use-authoring-legislation-library"
import { getDescendantFolderIds } from "../legislation-library-helpers"
import { LegislationLibraryChooseFolderRow } from "./legislation-library-choose-folder-row"
import { LegislationLibraryChooseFolderCrumb } from "./legislation-library-choose-folder-crumb"
import { LegislationLibraryChooseFolderActions } from "./legislation-library-choose-folder-actions"

export function LegislationLibraryChooseFolderDialog({ id }) {
    const { getRootIds, getNode, getChildren, getPath, state } = useAuthoringLegislationLibrary()
    const [browsingId, setBrowsingId] = useState(null)

    const excludedIds = new Set([id, ...getDescendantFolderIds(state, id)])

    const rawItems = browsingId ? getChildren(browsingId) : getRootIds().map(getNode).filter(Boolean)
    const items = sortBy([...rawItems], "name")
    const path = browsingId ? getPath(browsingId) : []
    const currentPath = browsingId ? path.map((node) => node.name).join(" › ") : "Library Root"
    const isAtExcludedFolder = browsingId !== null && excludedIds.has(browsingId)
    const confirmDisabled = isAtExcludedFolder
    const disabledReason = getDisabledReason()

    return (
        <DialogBox
            title="Move Folder"
            Actions={LegislationLibraryChooseFolderActions}
            browsingId={browsingId}
            currentPath={currentPath}
            confirmDisabled={confirmDisabled}
            disabledReason={disabledReason}
            data-cy="legislation-choose-folder-dialog"
        >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap" }}>
                <If value={browsingId === null}>
                    <Typography color="text.primary" variant="body2">
                        Library Root
                    </Typography>
                    <Else>
                        <Link
                            component="button"
                            type="button"
                            onClick={handleGoToRoot}
                            underline="hover"
                            color="inherit"
                            variant="body2"
                        >
                            Library Root
                        </Link>
                    </Else>
                </If>
                <Repeat list={path} item={<LegislationLibraryChooseFolderCrumb onNavigate={setBrowsingId} />} />
            </Stack>
            <Box sx={{ maxHeight: 320, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                <List dense data-cy="legislation-choose-folder-list">
                    <Repeat
                        list={items}
                        item={<LegislationLibraryChooseFolderRow onNavigate={setBrowsingId} />}
                        fallback={
                            <Typography color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                                This folder is empty.
                            </Typography>
                        }
                    />
                </List>
            </Box>
        </DialogBox>
    )

    function handleGoToRoot() {
        setBrowsingId(null)
    }

    function getDisabledReason() {
        if (!isAtExcludedFolder) return ""
        if (browsingId === id) return "This is the folder being moved."
        return "A folder cannot be moved into one of its subfolders."
    }
}
